import { Facet, StateEffect, StateField, type ChangeDesc, type Extension, type Text } from "@codemirror/state";
import { Decoration, EditorView, keymap, showPanel, type DecorationSet, type Panel, type ViewUpdate } from "@codemirror/view";
import { ySyncAnnotation } from "y-codemirror.next";
import { latexToSpeech, plainToSpeech, type Sentence, type SpeechContext } from "./speech";

/**
 * Read aloud: the browser's own speech voices read the open file from the cursor, sentence by
 * sentence, highlighting the sentence and the word being said. Typing pauses it, so you can fix
 * what you just heard; clicking a sentence while it reads jumps there.
 */

export type ReadMode = "latex" | "plain";
export type ReadStatus = "idle" | "loading" | "playing" | "paused" | "done";

export interface ReadAloudConfig {
  onPanel: (panel: ReadAloudPanel | null) => void;
  /** How the open file is read, or null if it can't be (code, data). */
  mode: () => ReadMode | null;
  /** Acronyms, macros and citations from the whole project. */
  context: () => Promise<SpeechContext>;
}

export function readMode(path: string): ReadMode | null {
  if (/\.(tex|ltx)$/i.test(path)) return "latex";
  if (/\.(txt|md|markdown)$/i.test(path)) return "plain";
  return null;
}

export const RATES = [0.75, 1, 1.25, 1.5, 1.75, 2];
/** Speaking pace at 1×, for the time-left estimate. */
const WORDS_PER_MINUTE = 165;

// ---------------------------------------------------------------- highlighting

const setMarks = StateEffect.define<{ sentence: [number, number] | null; word: [number, number] | null }>();
const setOpen = StateEffect.define<boolean>();
const sentenceMark = Decoration.mark({ class: "cm-ra-sentence" });
const wordMark = Decoration.mark({ class: "cm-ra-word" });

const marks = StateField.define<DecorationSet>({
  create: () => Decoration.none,
  update(deco, tr) {
    deco = deco.map(tr.changes);
    for (const e of tr.effects) {
      if (e.is(setOpen) && !e.value) deco = Decoration.none;
      if (!e.is(setMarks)) continue;
      const { sentence, word } = e.value;
      const ranges = [];
      if (sentence && sentence[1] > sentence[0]) ranges.push(sentenceMark.range(sentence[0], sentence[1]));
      if (word && word[1] > word[0]) ranges.push(wordMark.range(word[0], word[1]));
      deco = Decoration.set(ranges, true);
    }
    return deco;
  },
  provide: (f) => EditorView.decorations.from(f),
});

// ---------------------------------------------------------------- voices and settings

const PREFS_KEY = "benleaf.readAloud";

function loadPrefs(): { voice?: string; rate?: number } {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") ?? {};
  } catch {
    return {};
  }
}

function savePrefs(prefs: { voice: string | null; rate: number }) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // Private windows can refuse storage; the choice just won't be remembered.
  }
}

/** macOS's sound-effect voices, and Eloquence voices listed once per English locale. */
const NOVELTY = /^(Albert|Bad News|Bahh|Bells|Boing|Bubbles|Cellos|Good News|Jester|Organ|Superstar|Trinoids|Whisper|Wobble|Zarvox)$|^(Eddy|Flo|Grandma|Grandpa|Reed|Rocko|Sandy|Shelley) \(/;

/** English voices, Australian first, voices on this device before online ones, better versions first. */
export function englishVoices(): SpeechSynthesisVoice[] {
  const all = speechSynthesis.getVoices();
  const english = all.filter((v) => /^en([-_]|$)/i.test(v.lang) && !NOVELTY.test(v.name));
  const region = (v: SpeechSynthesisVoice) => {
    const lang = v.lang.replace("_", "-").toLowerCase();
    return lang === "en-au" ? 0 : /^en-(gb|nz|ie)/.test(lang) ? 1 : 2;
  };
  const quality = (v: SpeechSynthesisVoice) => (/premium/i.test(v.name) ? 0 : /enhanced|natural|neural/i.test(v.name) ? 1 : 2);
  const rank = (v: SpeechSynthesisVoice) => (v.localService ? 0 : 30) + region(v) * 3 + quality(v);
  return (english.length ? english : all).sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name));
}

const ERRORS: Record<string, string> = {
  "not-allowed": "The browser blocked speech. Press play to start it.",
  network: "That voice needs the internet. Pick another voice.",
  "synthesis-unavailable": "That voice isn't available. Pick another voice.",
  "synthesis-failed": "That voice couldn't read this. Pick another voice.",
  "voice-unavailable": "That voice isn't available. Pick another voice.",
  "language-unavailable": "That voice isn't available. Pick another voice.",
  "audio-busy": "Another app is using the speakers.",
  "audio-hardware": "No speakers or headphones were found.",
};

function withTimeout<T>(p: Promise<T>, ms: number, fallback: T): Promise<T> {
  return Promise.race([p.catch(() => fallback), new Promise<T>((resolve) => setTimeout(() => resolve(fallback), ms))]);
}

// ---------------------------------------------------------------- the panel and the reader

const panels = new WeakMap<EditorView, ReadAloudPanel>();

/** The player docked under the editor (ReadAloudBar renders into `dom`), and the reading itself. */
export class ReadAloudPanel implements Panel {
  readonly dom = document.createElement("div");
  readonly top = false;
  readonly supported = typeof speechSynthesis !== "undefined" && typeof SpeechSynthesisUtterance !== "undefined";
  status: ReadStatus = "idle";
  /** Why reading stopped, when it wasn't the reader's choice. */
  message: string | null = null;
  sentences: Sentence[] = [];
  /** The sentence being read or paused on, or -1. */
  index = -1;
  voices: SpeechSynthesisVoice[] = [];
  voiceURI: string | null;
  rate: number;

  private parsed: Text | null = null;
  /** Edits since the sentences were worked out, to map their positions onto the doc as it is now. */
  private drift: ChangeDesc | null = null;
  private context: SpeechContext | null = null;
  private utter: SpeechSynthesisUtterance | null = null;
  /** Each start bumps this, so a start still loading knows it's been overtaken. */
  private run = 0;
  /** The word being read, as a position in the doc; pausing resumes here. */
  private at = 0;
  private wordsSaid = 0;
  /** Reading a selection: stop at its end. */
  private limit: { from: number; to: number } | null = null;
  /** Where the cursor was when reading paused: moving it means "carry on from here". */
  private pausedHead = -1;
  /** Words before each sentence, for the time left. */
  private wordsBefore: number[] = [0];
  private version = 0;
  private listeners = new Set<() => void>();

  constructor(
    readonly view: EditorView,
    private conf: ReadAloudConfig,
  ) {
    this.dom.className = "cm-read-aloud";
    const prefs = loadPrefs();
    this.voiceURI = prefs.voice ?? null;
    this.rate = RATES.includes(prefs.rate ?? 1) ? (prefs.rate ?? 1) : 1;
    panels.set(view, this);
  }

  subscribe = (fn: () => void) => {
    this.listeners.add(fn);
    return () => void this.listeners.delete(fn);
  };
  getVersion = () => this.version;
  private changed() {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  mount() {
    this.conf.onPanel(this);
    if (!this.supported) return;
    this.loadVoices();
    speechSynthesis.addEventListener("voiceschanged", this.loadVoices);
  }

  destroy() {
    this.halt();
    if (this.supported) speechSynthesis.removeEventListener("voiceschanged", this.loadVoices);
    if (panels.get(this.view) === this) panels.delete(this.view);
    this.conf.onPanel(null);
  }

  update(u: ViewUpdate) {
    if (u.docChanged) {
      this.drift = this.drift ? this.drift.composeDesc(u.changes.desc) : u.changes.desc;
      this.at = u.changes.mapPos(this.at);
      if (this.pausedHead >= 0) this.pausedHead = u.changes.mapPos(this.pausedHead);
      if (this.limit) this.limit = { from: u.changes.mapPos(this.limit.from, 1), to: u.changes.mapPos(this.limit.to, -1) };
      // Typing pauses, so you can fix what you just heard. Collaborators' edits don't.
      if (this.status === "playing" && u.transactions.some((tr) => tr.docChanged && !tr.annotation(ySyncAnnotation))) queueMicrotask(() => this.pause());
    }
    if (u.selectionSet && this.status === "playing" && u.transactions.some((tr) => tr.isUserEvent("select.pointer"))) {
      const head = u.state.selection.main.head;
      queueMicrotask(() => this.status === "playing" && this.jumpTo(head));
    }
  }

  private loadVoices = () => {
    this.voices = englishVoices();
    this.changed();
  };

  voice(): SpeechSynthesisVoice | null {
    return this.voices.find((v) => v.voiceURI === this.voiceURI) ?? this.voices[0] ?? null;
  }

  /** Sentences left and roughly how long they'll take. */
  progress(): { sentence: number; total: number; minutesLeft: number } {
    const total = this.sentences.length;
    const i = Math.max(0, this.index);
    const words = (this.wordsBefore[total] ?? 0) - (this.wordsBefore[i] ?? 0);
    return { sentence: i + 1, total, minutesLeft: words / (WORDS_PER_MINUTE * this.rate) };
  }

  // ------------------------------------------------ controls

  /** Play from the cursor (just the selection, if there is one), or carry on after a pause. */
  play() {
    if (!this.supported || this.status === "playing" || this.status === "loading") return;
    const sel = this.view.state.selection.main;
    if (this.status === "paused") {
      if (sel.head === this.pausedHead) {
        void this.startAt(this.at, true);
        return;
      }
      // Moving the cursor while paused means "carry on from here".
      if (sel.empty) {
        this.limit = null;
        void this.startAt(sel.head, false);
        return;
      }
    }
    this.limit = sel.empty ? null : { from: sel.from, to: sel.to };
    void this.startAt(sel.from, !sel.empty);
  }

  toggle() {
    if (this.status === "playing" || this.status === "loading") this.pause();
    else this.play();
  }

  pause() {
    if (this.status === "loading") {
      this.run++;
      this.status = "idle";
      this.changed();
      return;
    }
    if (this.status !== "playing") return;
    this.silence();
    this.status = "paused";
    this.pausedHead = this.view.state.selection.main.head;
    this.changed();
  }

  stop() {
    this.halt();
    this.mark(null, null);
    this.changed();
  }

  next() {
    if (this.index < 0) return;
    const i = this.current();
    this.silence();
    this.continueAt(i + 1);
  }

  /** Back to the start of this sentence, or the one before if this one has only just begun. */
  prev() {
    if (this.index < 0) return;
    const i = this.current();
    const target = this.status === "playing" && this.wordsSaid > 2 ? i : Math.max(0, i - 1);
    this.silence();
    this.limit = null;
    this.speak(target, 0);
  }

  jumpTo(pos: number) {
    this.limit = null;
    void this.startAt(pos, false);
  }

  setRate(rate: number) {
    this.rate = rate;
    savePrefs({ voice: this.voiceURI, rate });
    this.changed();
    if (this.status === "playing") void this.startAt(this.at, true);
  }

  setVoice(uri: string) {
    this.voiceURI = uri;
    savePrefs({ voice: uri, rate: this.rate });
    this.changed();
    if (this.status === "playing") void this.startAt(this.at, true);
  }

  // ------------------------------------------------ reading

  private map(pos: number): number {
    return this.drift ? this.drift.mapPos(pos) : pos;
  }

  private parse() {
    const text = this.view.state.doc.toString();
    this.sentences = this.conf.mode() === "plain" ? plainToSpeech(text) : latexToSpeech(text, this.context ?? {});
    this.parsed = this.view.state.doc;
    this.drift = null;
    this.wordsBefore = [0];
    for (const s of this.sentences) this.wordsBefore.push(this.wordsBefore[this.wordsBefore.length - 1] + s.text.split(" ").length);
  }

  /** The sentence being read, re-reading the doc first if it has changed. */
  private current(): number {
    if (this.parsed !== this.view.state.doc) this.parse();
    const i = this.sentences.findIndex((s) => s.to > this.at);
    return i < 0 ? this.sentences.length - 1 : i;
  }

  /** The first word of `s` at or after `pos`. */
  private charAt(s: Sentence, pos: number): number {
    let k = 0;
    while (k < s.text.length && this.map(s.ends[k]) <= pos) k++;
    while (k > 0 && s.text[k - 1] !== " ") k--;
    return k >= s.text.length ? 0 : k;
  }

  /** From the sentence at `pos`: its start, or (exact) the word at `pos`. */
  private async startAt(pos: number, exact: boolean) {
    const run = ++this.run;
    this.silence();
    if (!this.context) {
      this.status = "loading";
      this.message = null;
      this.changed();
      this.context = await withTimeout(this.conf.context(), 2500, {});
      if (run !== this.run) return;
      this.parsed = null;
    }
    if (this.parsed !== this.view.state.doc) this.parse();
    let i = this.sentences.findIndex((s) => s.to > pos);
    // Past the last sentence: start again from the top.
    if (i < 0 && !this.limit) i = this.sentences.length ? 0 : -1;
    const s = this.sentences[i];
    if (!s || (this.limit && s.from >= this.limit.to)) {
      this.finish(this.sentences.length ? null : "There's nothing to read in this file.");
      return;
    }
    this.speak(i, exact || this.limit ? this.charAt(s, pos) : 0);
  }

  private speak(i: number, char: number) {
    const s = this.sentences[i];
    let end = s.text.length;
    if (this.limit) {
      let k = char;
      while (k < end && this.map(s.starts[k]) < this.limit.to) k++;
      end = k;
    }
    const text = s.text.slice(char, end);
    if (!text.trim()) {
      this.continueAt(i + 1);
      return;
    }
    this.index = i;
    this.wordsSaid = 0;
    this.at = this.map(s.starts[char]);
    this.status = "playing";
    this.message = null;
    this.mark(s, null, true);

    const u = new SpeechSynthesisUtterance(text);
    const voice = this.voice();
    if (voice) {
      u.voice = voice;
      u.lang = voice.lang;
    } else u.lang = "en-AU";
    u.rate = this.rate;
    u.onboundary = (e) => {
      if (this.utter !== u || e.name !== "word") return;
      const c = Math.min(char + e.charIndex, s.text.length - 1);
      const len = e.charLength || (/^\S+/.exec(s.text.slice(c))?.[0].length ?? 1);
      this.wordsSaid++;
      this.at = this.map(s.starts[c]);
      this.mark(s, [c, Math.min(c + len, s.text.length)]);
    };
    u.onend = () => {
      if (this.utter !== u) return;
      this.utter = null;
      this.advance(i);
    };
    u.onerror = (e) => {
      if (this.utter !== u) return;
      this.utter = null;
      if (e.error === "interrupted" || e.error === "canceled") return;
      this.status = "paused";
      this.pausedHead = this.view.state.selection.main.head;
      this.message = ERRORS[e.error] ?? `Reading stopped (${e.error}).`;
      this.changed();
    };
    // Chrome drops the callbacks of an utterance nothing refers to, so keep hold of it.
    this.utter = u;
    this.changed();
    speechSynthesis.speak(u);
  }

  private advance(i: number) {
    if (this.parsed !== this.view.state.doc) {
      // The text changed while that sentence was read: work the sentences out again and carry on after it.
      const after = this.map(this.sentences[i].to);
      this.parse();
      this.continueAt(this.sentences.findIndex((s) => s.from >= after));
      return;
    }
    this.continueAt(i + 1);
  }

  private continueAt(n: number) {
    const s = this.sentences[n];
    if (n < 0 || !s || (this.limit && this.map(s.from) >= this.limit.to)) this.finish(null);
    else this.speak(n, 0);
  }

  private finish(message: string | null) {
    this.status = "done";
    this.message = message;
    this.index = -1;
    this.limit = null;
    this.mark(null, null);
    this.changed();
  }

  private silence() {
    this.utter = null;
    if (this.supported) speechSynthesis.cancel();
  }

  /** Stop without touching the editor (it may be closing). */
  private halt() {
    this.run++;
    this.silence();
    this.status = "idle";
    this.message = null;
    this.index = -1;
    this.limit = null;
  }

  private visible(from: number, to: number): boolean {
    const box = this.view.scrollDOM.getBoundingClientRect();
    const a = this.view.coordsAtPos(from);
    const b = from === to ? a : this.view.coordsAtPos(to, -1);
    return !!a && !!b && a.top >= box.top && b.bottom <= box.bottom;
  }

  /** Highlights the sentence (and word), scrolling them into view if they've gone off screen. */
  private mark(s: Sentence | null, word: [number, number] | null, newSentence = false) {
    const len = this.view.state.doc.length;
    const clamp = (p: number) => Math.max(0, Math.min(len, p));
    const sentence: [number, number] | null = s ? [clamp(this.map(s.from)), clamp(this.map(s.to))] : null;
    const w: [number, number] | null = s && word ? [clamp(this.map(s.starts[word[0]])), clamp(this.map(s.ends[word[1] - 1]))] : null;
    const effects: StateEffect<unknown>[] = [setMarks.of({ sentence, word: w })];
    const target = w ?? (newSentence && sentence ? ([sentence[0], sentence[0]] as [number, number]) : null);
    if (target && !this.visible(target[0], target[1])) effects.push(EditorView.scrollIntoView(target[0], { y: "center" }));
    this.view.dispatch({ effects });
  }
}

// ---------------------------------------------------------------- the extension

const config = Facet.define<ReadAloudConfig, ReadAloudConfig | null>({ combine: (v) => v[0] ?? null });
const createPanel = (view: EditorView) => new ReadAloudPanel(view, view.state.facet(config)!);
const open = StateField.define<boolean>({
  create: () => false,
  update(on, tr) {
    for (const e of tr.effects) if (e.is(setOpen)) on = e.value;
    return on;
  },
  provide: (f) => showPanel.from(f, (on) => (on ? createPanel : null)),
});

export function canReadAloud(view: EditorView): boolean {
  return !!view.state.facet(config)?.mode();
}

/** Opens the player and starts reading; after that, pauses and resumes. */
export function toggleReadAloud(view: EditorView): boolean {
  if (!canReadAloud(view)) return false;
  const panel = panels.get(view);
  if (panel) panel.toggle();
  else {
    view.dispatch({ effects: setOpen.of(true) });
    panels.get(view)?.play();
  }
  return true;
}

export function closeReadAloud(view: EditorView) {
  view.dispatch({ effects: setOpen.of(false) });
}

export function readAloud(conf: ReadAloudConfig): Extension {
  return [config.of(conf), open, marks, keymap.of([{ key: "Mod-Shift-u", run: toggleReadAloud }])];
}
